/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { useCallback, useState } from "react";
import { useRecoilValue } from "recoil";
import { UserAtom } from "../../../atoms/UserAtom";
import { SavedSearch } from "../../../types/SavedSearchTypes";
import { ITagQuery } from "../../../types/TagQueryTypes";
import CreateEditSearchModal from "./SavedSearch/CreateEditSearchModal";
import EditTagsSearchModal from "./SavedSearch/EditTagsSearchModal";
import FooterSearchModal from "./SavedSearch/FooterSearchModal";
import ManageSearchModal from "./SavedSearch/ManageSearchModal";

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: hidden;
  overflow-x: hidden;
  border-radius: 4px;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 600px;
  height: 80vh;

  @media (max-height: 750px) {
    height: 90vh;
  }

  padding: 0px;
  position: relative;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: 100vh;
    max-height: 100%;
  }
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 0px 24px;
  margin: 78px 0px 90px;
  grid-gap: 10px;
`;

export const Header = styled.div`
  border-bottom: 1px solid #eceeee;
  padding: 10px 24px 10px 24px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  position: absolute;
  top: 0;
  background-color: white;
  border-radius: 4px 4px 0px 0px;
`;

export const HeaderTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: #2f2f2f;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.18px;
`;

export const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 24px;
  border-top: 1px solid #eceeee;
  width: 100%;
  position: absolute;
  bottom: 0;
  background-color: white;
  border-radius: 0px 0px 4px 4px;
`;

export enum SavedSearchModal {
  MANAGE = "MANAGE",
  NEW_EDIT = "NEW_EDIT",
  FOOTER = "FOOTER",
  RECIPIENT = "RECIPIENT",
}

type Props = {
  tagQuery?: ITagQuery;
  isEdit?: boolean;
};

export default function SavedSearchesModal(props: Props): JSX.Element {
  const user = useRecoilValue(UserAtom);
  const [page, setPage] = useState(
    props.isEdit ? SavedSearchModal.MANAGE : SavedSearchModal.NEW_EDIT
  );

  const [selectedSavedSearch, setSelectedSavedSearch] =
    useState<SavedSearch | null>({
      id: "",
      name: "",
      userUid: "",
      search: props.tagQuery
        ? props.tagQuery
        : {
            operator: "ANY_OF",
            tags: [],
          },
      footerId: user?.emailOptions.footers[0].id,
      publicArchive: false,
      labels: [],
    });

  const [isCreateFooter, setIsCreateFooter] = useState(false);

  const returnPage = useCallback(() => {
    switch (page) {
      case SavedSearchModal.MANAGE:
        return (
          <ManageSearchModal
            editSavedSearch={(search) => {
              setSelectedSavedSearch(search);
              setPage(SavedSearchModal.NEW_EDIT);
            }}
            editTagsSavedSearch={(search) => {
              setSelectedSavedSearch(search);
              setPage(SavedSearchModal.RECIPIENT);
            }}
          />
        );

      case SavedSearchModal.NEW_EDIT:
        return (
          <CreateEditSearchModal
            search={selectedSavedSearch}
            tagQuery={props.tagQuery}
            isEdit={props.isEdit}
            handlePage={(page) => setPage(page)}
            handleSavedSearch={(search) => setSelectedSavedSearch(search)}
            handleCreateFooter={(vle) => setIsCreateFooter(vle)}
          />
        );

      case SavedSearchModal.RECIPIENT:
        return (
          <EditTagsSearchModal
            search={selectedSavedSearch}
            handlePage={(page) => setPage(page)}
            handleSavedSearch={(search) => setSelectedSavedSearch(search)}
          />
        );

      case SavedSearchModal.FOOTER:
        return (
          <FooterSearchModal
            search={selectedSavedSearch}
            isEdit={!isCreateFooter}
            searchIsEdit={props.isEdit}
            handlePage={(page) => setPage(page)}
            handleSavedSearch={(search) => setSelectedSavedSearch(search)}
          />
        );
    }
  }, [selectedSavedSearch, page]);

  return <ModalBody>{returnPage()}</ModalBody>;
}
